import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inputCls = "w-full border border-[#E6DFD5] bg-white px-4 py-3 outline-none focus:border-[#8C4F27]";

const TXT = {
  id: {
    kicker: "MASUK DENGAN EMAIL", email: "Email", password: "Kata sandi", signin: "Masuk",
    current: "Kata sandi saat ini", next: "Kata sandi baru (min. 8 karakter)", confirm: "Ulangi kata sandi baru",
    update: "Perbarui kata sandi", mismatch: "Kata sandi baru tidak sama", updated: "Kata sandi berhasil diperbarui",
    forceTitle: "Buat kata sandi baru", forceDesc: "Anda masih memakai kata sandi awal. Silakan buat kata sandi sendiri sebelum melanjutkan.",
    change: "Ubah kata sandi",
    name: "Nama lengkap", register: "Daftar", registerKicker: "DAFTAR DENGAN EMAIL", pwMin: "Kata sandi (min. 8 karakter)",
    forgot: "Lupa kata sandi?", forgotTitle: "Lupa kata sandi", forgotDesc: "Masukkan email akun Anda. Kami akan mengirim link untuk membuat kata sandi baru.",
    sendLink: "Kirim link reset", sent: "Jika email terdaftar, link reset sudah kami kirim. Cek kotak masuk / spam.",
    resetTitle: "Buat kata sandi baru", resetBtn: "Simpan kata sandi baru", resetDone: "Kata sandi berhasil diganti. Silakan masuk.",
    errors: {
      "Email already registered": "Email sudah terdaftar. Silakan masuk atau gunakan Lupa kata sandi.",
      "Reset link is invalid or has expired": "Link reset tidak valid atau sudah kedaluwarsa",
      "Invalid email": "Email tidak valid",
      "Invalid email or password": "Email atau kata sandi salah",
      "Too many failed attempts. Try again in 15 minutes.": "Terlalu banyak percobaan gagal. Coba lagi dalam 15 menit.",
      "Current password is incorrect": "Kata sandi saat ini salah",
      "Choose a different password": "Pilih kata sandi yang berbeda dari sebelumnya",
    },
  },
  en: {
    kicker: "SIGN IN WITH EMAIL", email: "Email", password: "Password", signin: "Sign in",
    current: "Current password", next: "New password (min. 8 characters)", confirm: "Confirm new password",
    update: "Update password", mismatch: "Passwords do not match", updated: "Password updated",
    forceTitle: "Set a new password", forceDesc: "You are using the initial password. Please choose your own before continuing.",
    change: "Change password",
    name: "Full name", register: "Create account", registerKicker: "REGISTER WITH EMAIL", pwMin: "Password (min. 8 characters)",
    forgot: "Forgot password?", forgotTitle: "Forgot password", forgotDesc: "Enter your account email and we'll send you a link to set a new password.",
    sendLink: "Send reset link", sent: "If the email is registered, a reset link is on its way. Check your inbox / spam.",
    resetTitle: "Set a new password", resetBtn: "Save new password", resetDone: "Password changed. Please sign in.", errors: {},
  },
};

export const usePwText = () => {
  const { lang } = useApp();
  const tx = TXT[lang] || TXT.en;
  return { tx, err: (e) => tx.errors[errMsg(e)] || errMsg(e) };
};

export function PasswordLoginForm() {
  const { setUser } = useApp();
  const { tx, err } = usePwText();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/auth/login", { email, password }); setUser(r.data); }
    catch (x) { toast.error(err(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="password-login-form">
      <p className="text-xs font-mono tracking-[0.2em] text-[#8C4F27]">{tx.kicker}</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tx.email} className={inputCls} data-testid="login-email-input" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={tx.password} className={inputCls} data-testid="login-password-input" />
      <button disabled={busy} className="w-full bg-[#3B220C] text-white py-3 text-sm disabled:opacity-50" data-testid="login-submit-btn">{tx.signin}</button>
      <button type="button" onClick={() => setForgot(true)} className="text-sm underline text-[#6E6259]" data-testid="forgot-password-link">{tx.forgot}</button>
      <ForgotDialog open={forgot} onOpenChange={setForgot} initial={email} />
    </form>
  );
}

function ForgotDialog({ open, onOpenChange, initial }) {
  const { tx, err } = usePwText();
  const [email, setEmail] = useState(initial);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { await api.post("/auth/forgot-password", { email }); setDone(true); } catch (x) { toast.error(err(x)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); setDone(false); setEmail(initial); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="forgot-password-dialog">
        <DialogTitle className="font-serif text-3xl font-normal">{tx.forgotTitle}</DialogTitle>
        <DialogDescription>{tx.forgotDesc}</DialogDescription>
        {done ? <p className="text-sm text-[#2E6B40]" data-testid="forgot-password-sent">{tx.sent}</p> : (
          <form onSubmit={submit} className="space-y-3">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tx.email} className={inputCls} data-testid="forgot-email-input" />
            <button className="w-full bg-[#3B220C] text-white py-3 text-sm" data-testid="forgot-submit-btn">{tx.sendLink}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RegisterForm() {
  const { setUser } = useApp();
  const { tx, err } = usePwText();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/auth/register", f); setUser(r.data); }
    catch (x) { toast.error(err(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <p className="text-xs font-mono tracking-[0.2em] text-[#8C4F27]">{tx.registerKicker}</p>
      <input required value={f.name} onChange={s("name")} placeholder={tx.name} className={inputCls} data-testid="register-name-input" />
      <input type="email" required value={f.email} onChange={s("email")} placeholder={tx.email} className={inputCls} data-testid="register-email-input" />
      <input type="password" required minLength={8} value={f.password} onChange={s("password")} placeholder={tx.pwMin} className={inputCls} data-testid="register-password-input" />
      <button disabled={busy} className="w-full bg-[#3B220C] text-white py-3 text-sm disabled:opacity-50" data-testid="register-submit-btn">{tx.register}</button>
    </form>
  );
}

export function ResetPasswordForm({ token, onDone }) {
  const { tx, err } = usePwText();
  const [f, setF] = useState({ new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error(tx.mismatch); return; }
    try { await api.post("/auth/reset-password", { token, new_password: f.new_password }); toast.success(tx.resetDone); onDone(); }
    catch (x) { toast.error(err(x)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
      <input type="password" required minLength={8} placeholder={tx.next} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={inputCls} data-testid="reset-new-password-input" />
      <input type="password" required placeholder={tx.confirm} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={inputCls} data-testid="reset-confirm-password-input" />
      <button className="w-full bg-[#3B220C] text-white py-3 text-sm" data-testid="reset-submit-btn">{tx.resetBtn}</button>
    </form>
  );
}

export function ChangePasswordForm({ onDone }) {
  const { user, setUser } = useApp();
  const { tx, err } = usePwText();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error(tx.mismatch); return; }
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setUser({ ...user, must_change_password: false });
      toast.success(tx.updated);
      onDone?.();
    } catch (x) { toast.error(err(x)); }
  };
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <input type="password" required placeholder={tx.current} value={f.current_password} onChange={s("current_password")} className={inputCls} data-testid="current-password-input" />
      <input type="password" required minLength={8} placeholder={tx.next} value={f.new_password} onChange={s("new_password")} className={inputCls} data-testid="new-password-input" />
      <input type="password" required placeholder={tx.confirm} value={f.confirm} onChange={s("confirm")} className={inputCls} data-testid="confirm-password-input" />
      <button className="w-full bg-[#3B220C] text-white py-3 text-sm" data-testid="change-password-submit">{tx.update}</button>
    </form>
  );
}

export function ForcePasswordChange() {
  const { user } = useApp();
  const { tx } = usePwText();
  if (!user?.must_change_password || user.session_method !== "password") return null;
  return (
    <Dialog open>
      <DialogContent className="rounded-none max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="force-password-dialog">
        <DialogTitle className="font-serif text-3xl font-normal">{tx.forceTitle}</DialogTitle>
        <DialogDescription>{tx.forceDesc}</DialogDescription>
        <ChangePasswordForm onDone={() => window.location.reload()} />
      </DialogContent>
    </Dialog>
  );
}
